import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { TooltipProps } from 'recharts';
import {
  Cell,
  Label,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import Typography from '@mui/material/Typography';

const DISK_THRESHOLD = 80;
const COUNT_THRESHOLD = 100;

const getUsageColors = (
  percentage: number,
  theme: Theme,
  threshold: number | undefined = COUNT_THRESHOLD,
): [string, string] => {
  const backgroundColor = theme.palette.grey[200];

  if (!percentage) {
    return ['inherit', backgroundColor];
  }

  const isDiskThreshold = threshold === DISK_THRESHOLD;
  const showRedWarning = isDiskThreshold && percentage >= threshold;
  const showYellowWarning =
    (isDiskThreshold && percentage >= threshold - 10) ||
    (!isDiskThreshold && percentage >= COUNT_THRESHOLD);

  let usageColor = theme.palette.info.main;

  if (showRedWarning) {
    usageColor = theme.palette.error.main;
  } else if (showYellowWarning) {
    usageColor = theme.palette.warning.main;
  }

  return [usageColor, backgroundColor];
};

const getChartData = (used: number, total: number, t: TFunction) => {
  const remaining = Number((total - used).toFixed(2));
  return [
    { name: t('chartTooltip.used'), value: used },
    { name: t('chartTooltip.left'), value: remaining },
  ];
};

const CustomTooltip = ({
  active,
  payload,
  percentage,
  threshold = COUNT_THRESHOLD,
}: TooltipProps<number, string> & {
  percentage: number;
  threshold?: number;
}) => {
  const theme = useTheme();
  if (active && payload?.length) {
    const { name, value } = payload[0];
    const [tooltipColor] = getUsageColors(percentage, theme, threshold);
    return (
      <Paper sx={{ p: 1 }} elevation={2}>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ color: tooltipColor }}
        >
          {`${name} : ${value}`}
        </Typography>
      </Paper>
    );
  }
  return null;
};

type UsageChartProps = {
  used?: string | number | null;
  total?: string | number | null;
  percentage: number;
  threshold?: number;
  variant?: 'default' | 'compact';
};

const UsageChart = ({
  used = null,
  total = null,
  percentage,
  threshold = undefined,
  variant = 'default',
}: UsageChartProps) => {
  const { t } = useTranslation('hosting', { keyPrefix: 'hostingNova' });
  const theme = useTheme();
  const isCompact = variant === 'compact';
  const width = isCompact ? 52 : 80;
  const height = isCompact ? 48 : 80;

  const innerRadius = isCompact ? 20 : 32;
  const outerRadius = isCompact ? 24 : 40;

  const colors = getUsageColors(percentage, theme, threshold);
  return (
    <Stack
      sx={{
        position: 'relative',
        width,
        height,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={getChartData(
              Number(used ?? 0),
              Number.parseFloat(String(total ?? '0')),
              t,
            )}
            dataKey="value"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={90}
            endAngle={-270}
            stroke="none"
          >
            {colors.map((color, idx) => (
              <Cell key={`cell-${idx}`} fill={color} />
            ))}
            <Label
              value={percentage >= 100 ? '100' : `${percentage}%`}
              position="center"
              fontSize={14}
              fontWeight={600}
              fill="#000"
            />
          </Pie>
          <Tooltip<number, string>
            content={
              <CustomTooltip percentage={percentage} threshold={threshold} />
            }
          />
        </PieChart>
      </ResponsiveContainer>
    </Stack>
  );
};

export default UsageChart;
